<template>
  <div>
    <div
      v-if="qualification.verifiedAt && qualification.administrator"
      style="position: absolute; right: 1rem; margin-top: -2.4rem"
    >
      <v-tooltip bottom>
        <img slot="activator" src="/img/verified.png" width="30" />
        <span>
          Verified by {{ qualification.administrator.firstName }}
          {{ qualification.administrator.lastName }} on
          {{ new Date(qualification.verifiedAt) | date('d MMMM yyy') }}
        </span>
      </v-tooltip>
    </div>
    <div v-else style="position: absolute; right: 1rem; margin-top: -3rem;">
      <Flag v-for="(f, i) in flags" :key="`qualification-flag-${i}`" :value="f" icon />
      <can I="create" a="Flag">
        <CreateFlag
          :target="{ ...qualification, type: 'ProfileQualification' }"
          :profile="profile"
          :filter="['grade_value', 'qualification_dates']"
          @done="$emit('refresh')"
        >
          <v-btn icon flat color="error" slot="activator">
            <v-icon>mdi-flag</v-icon>
          </v-btn>
        </CreateFlag>
      </can>
    </div>

    <v-card flat>
      <v-layout row wrap align-center fill-height>
        <v-flex xs12 v-if="sortedMajors.length > 0">
          <div class="text-xs-center py-3 mt-2">
            <div class="title">
              <span
                v-if="qualification.qualificationType && qualification.qualificationType.acronym"
              >
                {{ qualification.qualificationType.acronym }}
              </span>
              <span v-else>{{ qualification.qualificationTypeName }}</span>
              <span v-if="qualification.specialization">
                in {{ qualification.specialization }}
              </span>
            </div>
            <div class="subheading" v-if="qualification.institutionName">
              at {{ qualification.institutionName }}
            </div>
            <span v-if="requiresProofOfRegistration" class="py-2" key="missing-proof">
              <v-icon left class="warning--text">mdi-alert-circle</v-icon>
              <span class="warning--text">Proof of Registration Required</span>
            </span>
            <span v-if="requiresQualificationTranscript" class="py-2" key="missing-transcript">
              <v-icon left color="warning">mdi-alert-circle</v-icon>
              <span class="warning--text">Transcript Required</span>
            </span>
          </div>
        </v-flex>
        <v-flex xs3 class="text-xs-center pt-3 mt-2" :class="dateColor">
          <CompletionIndicator
            :start="qualification.startsAt"
            :end="qualification.endsAt"
            :avatar="logo"
            :size="120"
          />
          <div v-if="qualification.startsAt || qualification.endsAt" class="pt-3">
            <span v-if="qualification.startsAt && qualification.startsAt !== qualification.endsAt">
              {{ new Date(qualification.startsAt) | date('MMM yyyy') }} -
            </span>
            <span v-if="qualification.endsAt">
              {{ new Date(qualification.endsAt) | date('MMM yyyy') }}
            </span>
          </div>
          <div v-if="qualification.accreditedAt" class="caption">
            <v-tooltip top color="grey darken-3">
              <span slot="activator">
                <v-icon small :class="dateColor">mdi-certificate</v-icon>
                {{ new Date(qualification.accreditedAt) | date('d MMMM yyyy') }}
              </span>
              <span>Accreditation Date</span>
            </v-tooltip>
          </div>
        </v-flex>
        <v-flex xs6 v-if="sortedMajors.length > 0" key="qualification-majors">
          <v-flex justify-center>
            <v-list subheader class="transparent">
              <div v-for="(component, i) in sortedMajors" :key="i">
                <v-subheader v-if="i < 1">{{ isSecondary ? 'Subjects' : 'Majors' }}</v-subheader>
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-avatar size="30" color="white">
                      <v-icon color="grey">mdi-check</v-icon>
                    </v-avatar>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ component.description }}</v-list-tile-title>
                    <v-list-tile-sub-title v-if="component.gradeValue">
                      {{ component.gradeValue }} {{ component.gradeScale }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </div>
            </v-list>
          </v-flex>
        </v-flex>
        <v-flex xs6 v-else key="qualification-description">
          <div class="text-xs-center py-3 mt-2">
            <div class="title">
              <span
                v-if="qualification.qualificationType && qualification.qualificationType.acronym"
              >
                {{ qualification.qualificationType.acronym }}
              </span>
              <span v-else>{{ qualification.qualificationTypeName }}</span>
              <span v-if="qualification.specialization">
                in {{ qualification.specialization }}</span
              >
            </div>
            <div class="subheading" v-if="qualification.institutionName">
              at {{ qualification.institutionName }}
            </div>
            <span v-if="requiresProofOfRegistration" class="py-2" key="missing-proof">
              <v-icon left class="warning--text">mdi-alert-circle</v-icon>
              <span class="warning--text">Proof of Registration Required</span>
            </span>
            <span v-if="requiresQualificationTranscript" class="py-2" key="missing-transcript">
              <v-icon left color="warning">mdi-alert-circle</v-icon>
              <span class="warning--text">Transcript Required</span>
            </span>
          </div>
        </v-flex>
        <v-flex v-if="qualification.gradeNormalized" class="pt-3 mt-2" xs3>
          <div class="text-xs-center">
            <v-progress-circular
              :value="Number(qualification.gradeNormalized)"
              :color="gradeColor"
              :size="120"
              :width="4"
              :rotate="-90"
            >
              <v-avatar size="110">
                <span class="display-1" :class="`${gradeColor}--text`">
                  {{ qualification.gradeValue }}
                </span>
              </v-avatar>
            </v-progress-circular>
            <div v-if="qualification.gradeScale" :class="`${gradeColor}--text`" class="pt-3">
              {{ qualification.gradeScale }}
            </div>
          </div>
        </v-flex>
      </v-layout>
    </v-card>
  </div>
</template>

<script>
// Components
import CompletionIndicator from '@/components/applications_tracker/profile/qualifications/CompletionIndicator'
import CreateFlag from '@/components/flags/CreateFlag'
import Flag from '@/components/flags/Flag'

// Helpers
import { byGradeValue } from '@/sorting/QualificationComponents'

export default {
  components: { CompletionIndicator, CreateFlag, Flag },

  props: {
    profile: { type: Object, required: true },
    qualification: { type: Object, required: true },
    flags: { type: Array, required: true },
    requiresProofOfRegistration: { type: Boolean, default: () => false },
    requiresQualificationTranscript: { type: Boolean, default: () => false },
  },

  computed: {
    dateColor() {
      const datesFlagged = this.flags.some(f => f.key.indexOf('qualification_dates') >= 0)
      return datesFlagged ? 'warning--text' : 'grey-text'
    },
    gradeColor() {
      const gradeFalgged = this.flags.some(f => f.key.indexOf('grade_value') >= 0)
      return gradeFalgged ? 'error' : 'secondary'
    },
    isSecondary() {
      return (
        _.get(this.qualification, 'qualificationType.qualificationCategoryName') ===
        'Secondary Education'
      )
    },
    logo() {
      return _.get(this.qualification, 'institution.logo')
    },
    sortedMajors() {
      if (!_.get(this.qualification, 'qualificationComponents')) return []

      const components = JSON.parse(JSON.stringify(this.qualification.qualificationComponents))
      return components.filter(c => this.isSecondary || c.disciplines[0]).sort(byGradeValue)
    },
  },
}
</script>
